import FooterPrivacy from '../../auth/components/baseTemplate/FooterPrivacy';
import { getPermissionsMap } from '../../../utils/helpers';
import { NavLink } from 'react-router-dom';
import { Permissions } from '../../../utils/enums/permissions.enum';
import { useLocalization } from 'src/utils/useLocalization';
import { useEffect, useMemo, useState } from 'react';
import './styles.scss';

export enum Tabs {
    INTERNAL = 'Internal',
    CONTENT = 'Content',
}
const FIELD_USER = 'field';
const SYSTEM_ADMIN_TAB_STORAGE_KEY = 'systemAdminActiveTab';

const SystemAdminPage = () => {
    const PermissionMap = useMemo(() => getPermissionsMap(), []);

    const dealerMaintenance =
        PermissionMap.has(Permissions.DEALER_SECURITY_MAINTENANCE) ||
        PermissionMap.get(Permissions.DEALER_SECURITY_MAINTENANCE)?.includes(
            FIELD_USER,
        );

    const dealerActivation =
        PermissionMap.has(Permissions.DEALER_ACTIVATION) ||
        PermissionMap.get(Permissions.DEALER_ACTIVATION)?.includes(FIELD_USER);

    const fieldAccessMaintenance = PermissionMap.has(
        Permissions.FIELD_ACCESS_MAINTENANCE,
    );
    const internalSecurityMaintenance = PermissionMap.has(
        Permissions.INTERNAL_SECURITY_MAINTENANCE,
    );

    const contentMaintenancePermission = PermissionMap.has(
        Permissions.CONTENT_MAINTENANCE_PERMISSION,
    );
    const distributionRulePermission = PermissionMap.has(
        Permissions.DISTRIBUTION_RULE_MANAGEMENT,
    );
    const NewsStoryManagementPermission = PermissionMap.has(
        Permissions.NEWS_STORY_MANAGEMENT,
    );
    const OnlineBulletinsMaintenancePermission = PermissionMap.has(
        Permissions.ONLINE_BULLETINS_MAINTENANCE,
    );

    const vehicleMaintenancePermission = PermissionMap.has(
        Permissions.VEHICLE_MAINTENANCE,
    );

    const showTabInternal =
        internalSecurityMaintenance ||
        fieldAccessMaintenance ||
        dealerMaintenance;

    const showTabContent =
        NewsStoryManagementPermission ||
        OnlineBulletinsMaintenancePermission ||
        contentMaintenancePermission ||
        vehicleMaintenancePermission;

    const getInitialTab = () => {
        const storedTab = localStorage.getItem(SYSTEM_ADMIN_TAB_STORAGE_KEY);
        if (storedTab && Object.values(Tabs).includes(storedTab as Tabs)) {
            return storedTab as Tabs;
        }
        return showTabInternal ? Tabs.INTERNAL : Tabs.CONTENT;
    };

    const [tab, setTab] = useState<Tabs>(getInitialTab());
    const { systemAdmin } = useLocalization();

    useEffect(() => {
        localStorage.setItem(SYSTEM_ADMIN_TAB_STORAGE_KEY, tab);
    }, [tab]);

    return (
        <div className='system-admin-wrapper'>
            <div className='page-title'>
                <h5>{systemAdmin.TITLE}</h5>
            </div>
            <div className={'content-container'}>
                <div className='c-tabs'>
                    <nav aria-label='Tabs Navigation'>
                        <div className='c-tabs__nav'>
                            {showTabInternal && (
                                <div>
                                    <button
                                        className={`c-tabs__nav-link ${
                                            tab == Tabs.INTERNAL
                                                ? 'is-active'
                                                : ''
                                        }`}
                                        onClick={() => {
                                            setTab(Tabs.INTERNAL);
                                        }}
                                        type='button'
                                    >
                                        <a
                                            className='c-link'
                                            data-testid='link-item-faqs'
                                        >
                                            <span className='c-link__body'>
                                                {systemAdmin.TABS_INTERNAL}
                                            </span>
                                        </a>
                                    </button>
                                </div>
                            )}
                            {showTabContent && (
                                <div>
                                    <button
                                        className={`c-tabs__nav-link ${
                                            tab == Tabs.CONTENT
                                                ? 'is-active'
                                                : ''
                                        }`}
                                        onClick={() => {
                                            setTab(Tabs.CONTENT);
                                        }}
                                        type='button'
                                    >
                                        <a
                                            className='c-link'
                                            data-testid='link-item-message-center'
                                        >
                                            <span className='c-link__body'>
                                                {systemAdmin.TABS_CONTENT}
                                            </span>
                                        </a>
                                    </button>
                                </div>
                            )}
                        </div>
                    </nav>
                </div>
                <div className='table-title'>
                    <div className='dealer-management-container'>
                        {showTabInternal && tab == Tabs.INTERNAL && (
                            <div className='internal-admin-wrapper'>
                                {(dealerActivation || dealerMaintenance) && (
                                    <div>
                                        <h6>{systemAdmin.DEALER_MANAGEMENT}</h6>
                                        <ul>
                                            {dealerActivation && (
                                                <li>
                                                    <NavLink
                                                        to={'dealer-activation'}
                                                    >
                                                        {
                                                            systemAdmin.DEALER_ACTIVATION
                                                        }
                                                    </NavLink>
                                                </li>
                                            )}
                                            {dealerMaintenance && (
                                                <>
                                                    <li>
                                                        <NavLink
                                                            to={
                                                                'dealer-security-maintenance'
                                                            }
                                                        >
                                                            {
                                                                systemAdmin.DEALER_SECURITY_MAINTENANCE
                                                            }
                                                        </NavLink>
                                                    </li>
                                                    {internalSecurityMaintenance && (
                                                        <li>
                                                            <NavLink
                                                                to={
                                                                    'dealer-group-maintenance'
                                                                }
                                                            >
                                                                {
                                                                    systemAdmin.DEALER_GROUP_MAINTENANCE
                                                                }
                                                            </NavLink>
                                                        </li>
                                                    )}
                                                </>
                                            )}
                                        </ul>
                                    </div>
                                )}
                                {(fieldAccessMaintenance ||
                                    internalSecurityMaintenance) && (
                                    <div>
                                        <h6>{systemAdmin.ACCESS_SECURITY}</h6>
                                        <ul>
                                            {fieldAccessMaintenance && (
                                                <li>
                                                    <NavLink
                                                        to={
                                                            'field-user-management'
                                                        }
                                                    >
                                                        {
                                                            systemAdmin.FIELD_USER_MANAGEMENT
                                                        }
                                                    </NavLink>
                                                </li>
                                            )}
                                            {internalSecurityMaintenance && (
                                                <li>
                                                    <NavLink
                                                        to={
                                                            'internal-security-maintenance'
                                                        }
                                                    >
                                                        {
                                                            systemAdmin.INTERNAL_SECURITY_MAINTENANCE
                                                        }
                                                    </NavLink>
                                                </li>
                                            )}
                                        </ul>
                                    </div>
                                )}
                                {internalSecurityMaintenance && (
                                    <div>
                                        <h6>{systemAdmin.INTERNAL_TOOLS}</h6>
                                        <ul>
                                            <li>
                                                <NavLink
                                                    to={'date-term-change'}
                                                >
                                                    {
                                                        systemAdmin.DATE_AND_TERM_CHANGE
                                                    }
                                                </NavLink>
                                            </li>
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                        {showTabContent && tab == Tabs.CONTENT && (
                            <div className='content-admin-wrapper'>
                                {/* Add new permission checks here if more Documents links are added */}
                                {contentMaintenancePermission && (
                                    <div>
                                        <h6>{systemAdmin.DOCUMENTS}</h6>
                                        <ul>
                                            <li>
                                                <NavLink
                                                    to={
                                                        'static-pdf-maintenance'
                                                    }
                                                >
                                                    {
                                                        systemAdmin.STATIC_PDF_MAINTENANCE
                                                    }
                                                </NavLink>
                                            </li>
                                        </ul>
                                    </div>
                                )}
                                {distributionRulePermission && (
                                    <div>
                                        <h6>
                                            {
                                                systemAdmin.DISTRIBUTION_RULE_MANAGEMENT
                                            }
                                        </h6>
                                        <ul>
                                            <li>
                                                <NavLink
                                                    to={
                                                        'create-distribution-rule'
                                                    }
                                                >
                                                    {
                                                        systemAdmin.CREATE_NEW_RULE
                                                    }
                                                </NavLink>
                                            </li>
                                            <li>
                                                <NavLink
                                                    to={
                                                        'view-distribution-rule'
                                                    }
                                                >
                                                    {
                                                        systemAdmin.VIEW_EDIT_EXISTING_RULE
                                                    }
                                                </NavLink>
                                            </li>
                                        </ul>
                                    </div>
                                )}
                                {OnlineBulletinsMaintenancePermission && (
                                    <div>
                                        <h6>{systemAdmin.BULLETINS_SECTION}</h6>
                                        <ul>
                                            <li>
                                                <NavLink to={'upload-bulletin'}>
                                                    {
                                                        systemAdmin.BULLETINS_UPLOAD_LINK
                                                    }
                                                </NavLink>
                                            </li>
                                            <li>
                                                <NavLink to={'view-bulletin'}>
                                                    {
                                                        systemAdmin.BULLETINS_VIEW_LINK
                                                    }
                                                </NavLink>
                                            </li>
                                        </ul>
                                    </div>
                                )}
                                {NewsStoryManagementPermission && (
                                    <div>
                                        <h6>{systemAdmin.COU_SECTION}</h6>
                                        <ul>
                                            <li>
                                                <NavLink
                                                    to={
                                                        'create-critical-organization-updates'
                                                    }
                                                >
                                                    {
                                                        systemAdmin.COU_CREATE_LINK
                                                    }
                                                </NavLink>
                                            </li>
                                            <li>
                                                <NavLink
                                                    to={
                                                        'view-critical-organization-updates'
                                                    }
                                                >
                                                    {systemAdmin.COU_VIEW_LINK}
                                                </NavLink>
                                            </li>
                                        </ul>
                                    </div>
                                )}
                                {vehicleMaintenancePermission && (
                                    <div>
                                        <h6>
                                            {
                                                systemAdmin.VEHICLE_MANAGER_SECTION
                                            }
                                        </h6>
                                        <ul>
                                            <li>
                                                <NavLink
                                                    to={'upload-vehicle-info'}
                                                >
                                                    {
                                                        systemAdmin.VEHICLE_UPLOAD_LINK
                                                    }
                                                </NavLink>
                                            </li>
                                            <li>
                                                <NavLink
                                                    to={'search-vehicle-info'}
                                                >
                                                    {
                                                        systemAdmin.VEHICLE_SEARCH_LINK
                                                    }
                                                </NavLink>
                                            </li>
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className='privacy-footer-container '>
                <FooterPrivacy />
            </div>
        </div>
    );
};
export default SystemAdminPage;
